import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { useLazyValidatePromoCodeQuery } from '@/components/orders/services/orderApi';
import { isPromoActive, computePromoDiscount } from '@/components/orders/payment/utils/promoDiscount';
import type { Promocode } from '@/types/order.types';
import { useSelectedOrgName } from '@/hooks/useEntityScope';

/**
 * Promo-code state for the Order Service modal's "Place order now" flow.
 * Validation hits the same endpoint as cart checkout; the discount is derived
 * from the LIVE subtotal so removing a photo re-applies it automatically.
 */
export function useAssetOrderPromo(subtotal: number) {
  const dispatch = useDispatch();
  const selectedOrgName = useSelectedOrgName() ?? '';
  const [triggerValidatePromo] = useLazyValidatePromoCodeQuery();

  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<Promocode | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputResetKey, setInputResetKey] = useState(0);

  const apply = useCallback(async () => {
    if (subtotal <= 0 || !promoCode) {
      setIsInvalid(true);
      setMessage('Please enter promo code');
      return;
    }
    setLoading(true);
    try {
      const entity = selectedOrgName;
      const result = await triggerValidatePromo({ promocode: promoCode, entity }).unwrap();
      const promo = result?.[0];
      if (promo && isPromoActive(promo) && promo.discountValue) {
        setAppliedPromo(promo);
        setIsInvalid(false);
        setMessage('');
      } else {
        setIsInvalid(true);
        setMessage('Promo code has expired');
      }
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      if (error?.data?.message?.includes('Promocode not applicable')) {
        setIsInvalid(true);
        setMessage('Promo code has expired');
      } else if (error?.data?.message?.includes('Invalid Promocode')) {
        setIsInvalid(true);
        setMessage('Invalid promo code');
      } else {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to validate promo code' }));
      }
    } finally {
      setLoading(false);
    }
  }, [subtotal, promoCode, triggerValidatePromo, dispatch]);

  const remove = useCallback(() => {
    setAppliedPromo(null);
    setPromoCode(null);
    setIsInvalid(false);
    setMessage('');
    setInputResetKey((k) => k + 1);
  }, []);

  const { savings, discountedTotal } = useMemo(
    () => (appliedPromo ? computePromoDiscount(appliedPromo, subtotal) : { savings: 0, discountedTotal: subtotal }),
    [appliedPromo, subtotal],
  );

  return {
    promoCode, setPromoCode,
    isApplied: appliedPromo != null,
    isInvalid, message, loading, inputResetKey,
    savings, discountedTotal,
    promoCodeId: appliedPromo?.promocodeId ?? null,
    apply, remove,
  };
}
